import type { AbilityId, ClassId } from './types';

export type AbilitySlot = 0 | 1;

/**
 * How an ability resolves when it is cast. The server owns the mechanics; the
 * client only needs this to pick a visual.
 */
export type AbilityKind =
  /** Damage everything in a circle around the caster. */
  | 'burst'
  /** Dash along the aim, damaging what the caster passes through. */
  | 'dash'
  /** Fan several projectiles out along the aim. */
  | 'volley'
  /** A single heavy projectile that pierces through monsters. */
  | 'bolt'
  /** Teleport along the aim, stopping short of anything solid. */
  | 'blink';

export interface AbilityDef {
  id: AbilityId;
  name: string;
  description: string;
  /** Key it is bound to. */
  key: string;
  cooldown: number;
  mpCost: number;
  colour: string;
  kind: AbilityKind;
  /** burst / dash: how wide the hit area is. */
  radius?: number;
  /** Multiplier applied to the caster's attack. */
  damageMult?: number;
  /** dash / blink: how far the caster travels. */
  distance?: number;
  /** volley: how many projectiles. */
  arrows?: number;
  /** volley: total spread in radians. */
  spread?: number;
}

export const ABILITIES: Record<AbilityId, AbilityDef> = {
  whirlwind: {
    id: 'whirlwind',
    name: 'Whirlwind',
    description: 'Spin on the spot, cutting every monster around you.',
    key: 'Q',
    cooldown: 6,
    mpCost: 16,
    colour: '#f0a868',
    kind: 'burst',
    radius: 245,
    damageMult: 1.7,
  },
  charge: {
    id: 'charge',
    name: 'Charge',
    description: 'Lunge forward, trampling everything in your path.',
    key: 'R',
    cooldown: 9,
    mpCost: 14,
    colour: '#e8845a',
    kind: 'dash',
    radius: 120,
    distance: 440,
    damageMult: 1.9,
  },
  volley: {
    id: 'volley',
    name: 'Volley',
    description: 'Loose a fan of arrows in a wide arc.',
    key: 'Q',
    cooldown: 5,
    mpCost: 16,
    colour: '#a8e06a',
    kind: 'volley',
    damageMult: 0.8,
    arrows: 5,
    spread: 0.62,
  },
  pierce: {
    id: 'pierce',
    name: 'Piercing Shot',
    description: 'A heavy arrow that punches through everything in a line.',
    key: 'R',
    cooldown: 8,
    mpCost: 20,
    colour: '#d8f07a',
    kind: 'bolt',
    damageMult: 2.4,
  },
  nova: {
    id: 'nova',
    name: 'Arcane Nova',
    description: 'A blast of raw magic erupts around you.',
    key: 'Q',
    cooldown: 7,
    mpCost: 24,
    colour: '#b39dff',
    kind: 'burst',
    radius: 285,
    damageMult: 1.85,
  },
  blink: {
    id: 'blink',
    name: 'Blink',
    description: 'Slip through space a short distance toward your aim.',
    key: 'R',
    cooldown: 6,
    mpCost: 14,
    colour: '#c9a7ff',
    kind: 'blink',
    distance: 450,
  },
};

/** The two abilities granted to each class, in quick bar order. */
export const CLASS_ABILITIES: Record<ClassId, [AbilityId, AbilityId]> = {
  warrior: ['whirlwind', 'charge'],
  ranger: ['volley', 'pierce'],
  mage: ['nova', 'blink'],
};

export function abilitiesFor(classId: ClassId): [AbilityDef, AbilityDef] {
  const ids = CLASS_ABILITIES[classId] ?? CLASS_ABILITIES.warrior;
  return [ABILITIES[ids[0]], ABILITIES[ids[1]]];
}
